import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { viewerColors } from "@/utils/viewer-colors";
import {
  BLACK,
  ZOOM_FACTOR,
  RENDERING_ORDER,
  MOUNTING_BUCKET,
  MARKER_DEPTH_BIAS_M,
} from "../constants";
import { markAsAnnotation } from "@/utils/annotation-depth";
import { roundVector } from "@/utils/round-vector.js";
import snapCursor from "@/assets/model/snap_cursor.svg";
import trashRed from "@/assets/model/trash_red.svg";
import { InstancedMesh2 } from "@three.ez/instanced-mesh";


const activeMountingBucket = (area) => {
  const key = MOUNTING_BUCKET[area?.activeMountingMode];
  return key ? area?.mountingConfigurations?.[key] ?? null : null;
};

export const getCurrentAnchor = (area) => {
  const anchor = activeMountingBucket(area)?.current_anchor;
  return anchor ? roundVector(anchor) : null;
};

export const setCurrentAnchor = (area, point) => {
  const bucket = activeMountingBucket(area);
  if (bucket) bucket.current_anchor = point;
};

function hexToRGB(color) {
  const r = ((color >> 16) & 0xff) / 255;
  const g = ((color >> 8) & 0xff) / 255;
  const b = (color & 0xff) / 255;
  return { r, g, b };
}

export const createShaderMaterial = function (
  innerColorHex,
  outerColorHex,
  threshold
) {
  const vertexShader = `
  varying vec3 vViewPosition;
  varying vec3 vNormal;

  void main() {
    vNormal = normalMatrix * normal;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;
  }
  `;

  const fragmentShader = `
  uniform vec3 innerColor;
  uniform vec3 outerColor;
  uniform float rimThreshold;
  // 1 normally; lowered while the occluded part of a point is drawn faintly
  // (renderAnnotatedFrame in @/utils/annotation-depth).
  uniform float opacity;
  varying vec3 vViewPosition;
  varying vec3 vNormal;

  void main() {
    vec3 normal = normalize(vNormal);
    vec3 viewDir = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);

    float rim = 1.0 - abs(dot(normal, viewDir));

    vec3 color = innerColor;
    float alpha = opacity;

    if (rim > rimThreshold) {
      color = outerColor;
    }

    gl_FragColor = vec4(color, alpha);
  }
  `;

  const innerColorRgb = hexToRGB(innerColorHex);
  const innerColor = new THREE.Color(
    innerColorRgb.r,
    innerColorRgb.g,
    innerColorRgb.b
  );

  const shaderMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      innerColor: { value: innerColor },
      outerColor: { value: new THREE.Color(outerColorHex) },
      rimThreshold: { value: threshold },
      opacity: { value: 1.0 },
    },
    transparent: true,
    opacity: 1.0,
  });

  return shaderMaterial;
};

export const createShaderMaterialForInstancedMesh = function (
  innerColorHex,
  outerColorHex,
  threshold
) {
  const vertexShader = `
  #include <get_from_texture>
  #include <instanced_pars_vertex>

  varying vec3 vViewPosition;
  varying vec3 vNormal;

  void main() {
    #include <instanced_vertex>
    
    vNormal = normalMatrix * normal;
    vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;
  }
  `;

  const fragmentShader = `
  uniform vec3 innerColor;
  uniform vec3 outerColor;
  uniform float rimThreshold;
  // 1 normally; lowered while the occluded part of a point is drawn faintly
  // (renderAnnotatedFrame in @/utils/annotation-depth).
  uniform float opacity;
  varying vec3 vViewPosition;
  varying vec3 vNormal;

  void main() {
    vec3 normal = normalize(vNormal);
    vec3 viewDir = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);

    float rim = 1.0 - abs(dot(normal, viewDir));

    vec3 color = innerColor;
    float alpha = opacity;

    if (rim > rimThreshold) {
      color = outerColor;
    }

    gl_FragColor = vec4(color, alpha);
  }
  `;

  const innerColorRgb = hexToRGB(innerColorHex);
  const innerColor = new THREE.Color(
    innerColorRgb.r,
    innerColorRgb.g,
    innerColorRgb.b
  );

  const shaderMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      innerColor: { value: innerColor },
      outerColor: { value: new THREE.Color(outerColorHex) },
      rimThreshold: { value: threshold },
      opacity: { value: 1.0 },
    },
    transparent: true,
    opacity: 1.0,
  });

  return shaderMaterial;
};

export const createReactivePoint = function (
  referencePoint,
  color,
  addSnapIcon = false
) {
  const material = this.createShaderMaterial(color, BLACK, 0.25);

  const point = new THREE.Mesh(this.dotGeometry, material);

  point.position.copy(referencePoint);

  if (addSnapIcon) {
    const icon = this.createSnapIcon();
    point.add(icon);
  }

  markAsAnnotation(point, {
    renderOrder: RENDERING_ORDER.OUTER_POINT,
    depthBias: MARKER_DEPTH_BIAS_M,
  });

  return point;
};

export const createSnapIcon = function () {
  const iconHtml = `<img class="snap-icon" src="${snapCursor}" />`;
  const iconElement = document.createElement("div");
  iconElement.innerHTML = iconHtml;
  iconElement.style.visibility = "hidden";

  const iconLabel = new CSS2DObject(iconElement);

  return iconLabel;
};

export const createReactiveMidPoint = function (
  startPoint,
  endPoint,
  color = BLACK
) {
  const position = new THREE.Vector3().lerpVectors(
    startPoint.position,
    endPoint.position,
    0.5
  );

  const material = this.createShaderMaterial(BLACK, color, 0.3);

  const midpoint = new THREE.Mesh(this.middotGeometry, material);

  midpoint.position.copy(position);
  markAsAnnotation(midpoint, {
    renderOrder: RENDERING_ORDER.OUTER_POINT,
    depthBias: MARKER_DEPTH_BIAS_M,
  });

  this.scene.add(midpoint);

  return midpoint;
};

export const createLabelBetweenTwoPoints = function (
  firstPoint,
  secondPoint,
  spacing,
  removable = false,
  line,
  offset = false
) {
  const distance = secondPoint.distanceTo(firstPoint);
  let div = document.createElement("div");
  div.className = "measurementLabel";
  div.distance = distance;

  if (removable) {
    let span = document.createElement("span");
    span.textContent = `${this.$n(distance, "decimal")}`;

    let img = document.createElement("img");
    img.src = trashRed;
    img.className = "q-ml-xs cursor-pointer";
    img.style.width = "15px";
    img.style.display = "none";

    div.appendChild(span);
    div.appendChild(img);

    div.style.cursor = "pointer";
    div.addEventListener("pointerdown", () => {
      this.showMeasurement(line.id);
    });

    img.addEventListener("pointerdown", () => {
      this.deleteMeasurement(line.id);
    });
  } else {
    div.textContent = `${this.$n(distance, "decimal")}`;
  }
  let label = new CSS2DObject(div);
  label.position.lerpVectors(secondPoint, firstPoint, spacing);

  // The point on the geometry this label belongs to, before the cosmetic nudge
  // below moves it off the line. Occlusion is judged here rather than at the
  // drawn position — see updateAnnotationOcclusion in @/modules/measurement.
  label.occlusionAnchor = label.position.clone();

  if (offset) {
    const direction = new THREE.Vector3()
      .subVectors(secondPoint, firstPoint)
      .normalize();

    const up = new THREE.Vector3(0, -1, 0);
    const perpendicular = new THREE.Vector3()
      .crossVectors(direction, up)
      .normalize();

    label.position.addScaledVector(perpendicular, 0.5);
  }

  label.layers.set(0);
  // Every measurement label shares this rung on purpose: CSS2DRenderer only
  // falls back to camera distance when renderOrder ties, so a per-label
  // increment here would stack labels by age and keep the newest one in front
  // of nearer ones forever. Selection lifts one label out of the tie — see
  // setMeasurementLabelSelected in @/modules/measurement.
  label.renderOrder = RENDERING_ORDER.MEASUREMENT_LABEL;

  return label;
};

export const updateLabelBetweenTwoPoints = function (
  label,
  firstPoint,
  secondPoint,
  spacing,
  offset = false,
  removable = false,
  line
) {
  const distance = secondPoint.distanceTo(firstPoint);
  const div = label.element;
  div.distance = distance;

  if (removable) {
    const span = document.createElement("span");
    span.textContent = `${this.$n(distance, "decimal")}`;
    div.replaceChild(span, div.firstChild);
    span.addEventListener("pointerdown", () => {
      this.showMeasurement(line.id);
    });
  } else {
    div.textContent = `${this.$n(distance, "decimal")}`;
  }
  label.position.lerpVectors(secondPoint, firstPoint, spacing);

  if (label.occlusionAnchor) label.occlusionAnchor.copy(label.position);

  if (offset) {
    const direction = new THREE.Vector3()
      .subVectors(secondPoint, firstPoint)
      .normalize();

    const up = new THREE.Vector3(0, -1, 0);
    const perpendicular = new THREE.Vector3()
      .crossVectors(direction, up)
      .normalize();

    label.position.addScaledVector(perpendicular, 0.5);
  }
  return distance;
};

/**
 * The pixel size of the canvas, for LineMaterial.
 *
 * LineMaterial expands a line to a pixel width in its vertex shader and uses
 * `resolution` for two things: that width, and the `resolution.x / resolution.y`
 * aspect it corrects the expansion by. It used to be handed the *window* size,
 * but the canvas is only `getViewerWidth` of the window once a sidebar is open,
 * and it was never refreshed on resize. A wrong aspect makes a line's rendered
 * width depend on its direction on screen, which is what made area outlines look
 * thicker on some edges than on others.
 */
export const getViewportResolution = function (target = new THREE.Vector2()) {
  if (!this.renderer) return target.set(window.innerWidth, window.innerHeight);
  return this.renderer.getSize(target);
};

/** Re-syncs every line's pixel width after the canvas changes size. */
export const updateLineResolutions = function () {
  if (!this.scene) return;

  const resolution = this.getViewportResolution();

  this.scene.traverse((object) => {
    if (!object.material) return;

    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];

    for (const material of materials) {
      if (material.isLineMaterial) material.resolution.copy(resolution);
    }
  });
};

export const createReactiveThickLine = function (
  points,
  thickness,
  dashed = false,
  transparent = false,
  color = viewerColors.MIDPOINT_COLOR
) {
  const flatPoints = [];

  for (const vector of points) {
    flatPoints.push(vector.x, vector.y, vector.z);
  }
  let geometry = new LineGeometry();
  geometry.setPositions(flatPoints);

  const lineMaterial = new LineMaterial({
    color: color,
    linewidth: thickness,
    dashed,
    dashSize: 0.4,
    gapSize: 0.3,
    resolution: this.getViewportResolution(),
    transparent: true,
  });
  if (dashed) lineMaterial.defines.USE_DASH = "";
  if (transparent) lineMaterial.uniforms.opacity.value = 0.4;

  let line = new Line2(geometry, lineMaterial);
  line.computeLineDistances();

  markAsAnnotation(line, { renderOrder: RENDERING_ORDER.LINE });

  return line;
};

export const createClosedThickLine = function ({
  points,
  color = viewerColors.MIDPOINT_COLOR,
  thickness = 4.0,
}) {
  const flatPoints = [];

  for (const point of points) {
    flatPoints.push(point.position.x, point.position.y, point.position.z);
  }

  flatPoints.push(
    points[0].position.x,
    points[0].position.y,
    points[0].position.z
  );

  const geometry = new LineGeometry();
  geometry.setPositions(flatPoints);

  const lineMaterial = new LineMaterial({
    color: color,
    linewidth: thickness,
    dashed: false,
    gapSize: 0.3,
    resolution: this.getViewportResolution(),
    transparent: true,
  });

  const line = new Line2(geometry, lineMaterial);
  line.computeLineDistances();

  markAsAnnotation(line, { renderOrder: RENDERING_ORDER.LINE });

  return line;
};

export const updateLinePosition = function (line, points) {
  const flatPoints = [];

  for (const vector of points) {
    flatPoints.push(vector.x, vector.y, vector.z);
  }
  line.geometry.setPositions(flatPoints);

  line.computeLineDistances();
};

export const updateClosedLinePosition = function ({
  line,
  points,
  forceUpdate = false,
}) {
  const flatPoints = [];

  for (const point of points) {
    flatPoints.push(point.position.x, point.position.y, point.position.z);
  }

  flatPoints.push(
    points[0].position.x,
    points[0].position.y,
    points[0].position.z
  );

  const newBufferSize = flatPoints.length;
  const currentBufferSize = line.geometry.attributes.position.array.length;

  if (newBufferSize !== currentBufferSize || forceUpdate) {
    const newGeometry = new LineGeometry();
    newGeometry.setPositions(flatPoints);
    line.geometry.dispose();
    line.geometry = newGeometry;
  } else {
    line.geometry.setPositions(flatPoints);
  }

  line.computeLineDistances();
};

export const getCenterPoint = function (points) {
  const vectorPoints = points.map((point) => point.position);
  return this.getCenterPointFromVectors(vectorPoints);
};

export const getCenterPointFromVectors = function (points) {
  let centerX = 0;
  let centerY = 0;
  let centerZ = 0;

  if (points.length === 0) return new THREE.Vector3(centerX, centerY, centerZ);

  for (let i = 0; i < points.length; i++) {
    centerX += points[i].x;
    centerY += points[i].y;
    centerZ += points[i].z;
  }

  centerX /= points.length;
  centerY /= points.length;
  centerZ /= points.length;

  return new THREE.Vector3(centerX, centerY, centerZ);
};

export const generateIndices = function (points) {
  const shape = new THREE.Shape();
  const numPoints = points.length;
  let indices = [];

  // Create the shape by connecting points in order
  shape.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < numPoints; i++) {
    shape.lineTo(points[i].x, points[i].y);
  }

  // Triangulate the shape
  const triangles = THREE.ShapeUtils.triangulateShape(points, []);

  for (let i = 0; i < triangles.length; i++) {
    indices.push(triangles[i][0], triangles[i][1], triangles[i][2]);
  }
  return indices;
};

export const calculateTriangleArea = function (v1, v2, v3) {
  const e1 = new THREE.Vector3().subVectors(v2, v1);
  const e2 = new THREE.Vector3().subVectors(v3, v1);
  const crossProduct = new THREE.Vector3().crossVectors(e1, e2);
  const triangleArea = 0.5 * crossProduct.length();
  return triangleArea;
};

export const removeObjectFromScene = function (object) {
  const sceneObject = this.scene.getObjectById(object.id);

  if (!sceneObject) return;

  if (sceneObject.geometry) {
    sceneObject.geometry.dispose();
  }

  if (sceneObject.material) {
    sceneObject.material.dispose();
    if (sceneObject.material.map) sceneObject.material.map.dispose();
  }

  this.scene.remove(sceneObject);
};

export const hideObjectFromScene = function (object) {
  if (!object) return;
  const sceneObject = this.scene.getObjectById(object.id);
  if (sceneObject) sceneObject.visible = false;
};

export const removeObjectWithChildrenFromScene = function (object) {
  const sceneObject = this.scene.getObjectById(object.id);

  if (sceneObject) {
    while (sceneObject.children.length > 0) {
      let child = sceneObject.children[0];
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
      sceneObject.remove(child);
    }
    // Remove the line group from the scene
    this.scene.remove(sceneObject);

    if (sceneObject.geometry) sceneObject.geometry.dispose();
    if (sceneObject.material) sceneObject.material.dispose();
  }
};

export const removeArrayFromScene = function (objects) {
  objects.forEach((object) => {
    this.removeObjectWithChildrenFromScene(object);
  });
};

// Elements whose default action on click is the entire point of them: a radio
// has to be able to become checked, a link to navigate, a text field to take
// the caret. Anything else — the canvas, and the CSS2D label layer of divs, ps,
// spans and imgs drawn over it — has no default worth keeping.
const INTERACTIVE_CLICK_TARGETS = [
  "input",
  "textarea",
  "select",
  "option",
  "button",
  "a[href]",
  "label",
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(", ");

/**
 * Suppress a click's default action, unless it landed on a real control.
 *
 * The handlers that call this are registered on `document`, so suppressing
 * unconditionally also killed the activation of controls rendered over the
 * viewer — a radio button in a dialog could never be selected, because the
 * click reached it and then had its default behaviour taken away.
 *
 * The exception is deliberately narrow rather than "only the canvas": one of
 * these handlers is on `dblclick` (`addZoomCenter`), where the default action
 * is selecting a word of text. Suppressing that for the CSS2D labels drawn over
 * the model is behaviour worth keeping, so everything except the controls above
 * is still cancelled exactly as before.
 */
export const suppressClickDefault = function (event) {
  if (event.target?.closest?.(INTERACTIVE_CLICK_TARGETS)) return;
  event.preventDefault();
};

export const disableClick = function (event) {
  if (
    this.isShiftDown ||
    this.isAltDown ||
    event.target.tagName !== "CANVAS" ||
    event.button !== 0
  )
    return true;
  else return false;
};

// The canvas's on-screen rectangle, cached between the events that can move it.
//
// setMousePosition runs on every mouse move, from dozens of handlers, and used
// to call getBoundingClientRect() each time. CSS2DRenderer rewrites every
// label's transform each frame, so that call usually found the layout stale and
// forced the browser to recompute it on the spot — about a quarter of all
// main-thread time while orbiting with the mouse. The rectangle itself only
// changes when the canvas is resized or the page scrolls, so it is read once and
// re-read only after one of those.
//
// A generation counter rather than clearing entries, because a WeakMap cannot be
// cleared: anything that may have moved the canvas bumps the generation, and an
// entry from an older generation is simply re-read.
const canvasBoundsCache = new WeakMap();
const observedCanvases = new WeakSet();
let canvasBoundsGeneration = 0;
let canvasBoundsListenersInstalled = false;

const invalidateCanvasBounds = () => {
  canvasBoundsGeneration += 1;
};

function getCanvasBounds(canvas) {
  if (!canvasBoundsListenersInstalled) {
    // Window resizes cover browser zoom and DevTools docking too. Scroll is
    // captured so that scrolling any ancestor counts, not just the window.
    window.addEventListener("resize", invalidateCanvasBounds, { passive: true });
    window.addEventListener("scroll", invalidateCanvasBounds, {
      passive: true,
      capture: true,
    });
    canvasBoundsListenersInstalled = true;
  }

  // Catches resizes that never reach a window resize: the sidebar opening or
  // closing (onWindowResize → renderer.setSize), CSS changes, being hidden and
  // shown again.
  if (!observedCanvases.has(canvas) && typeof ResizeObserver !== "undefined") {
    new ResizeObserver(invalidateCanvasBounds).observe(canvas);
    observedCanvases.add(canvas);
  }

  const cached = canvasBoundsCache.get(canvas);
  if (cached && cached.generation === canvasBoundsGeneration) return cached.rect;

  const rect = canvas.getBoundingClientRect();
  canvasBoundsCache.set(canvas, { rect, generation: canvasBoundsGeneration });
  return rect;
}

export const setMousePosition = function (event) {
  const canvasBounds = getCanvasBounds(this.renderer.getContext().canvas);
  this.mouse.x =
    ((event.clientX - canvasBounds.left) /
      (canvasBounds.right - canvasBounds.left)) *
      2 -
    1;
  this.mouse.y =
    -(
      (event.clientY - canvasBounds.top) /
      (canvasBounds.bottom - canvasBounds.top)
    ) *
      2 +
    1;

  this.raycaster.setFromCamera(this.mouse, this.camera);
};

export const restoreDefaultCursor = function () {
  this.renderer.domElement.style.cursor = `default`;
};

export const displayAreaPoint = function (element, isMeasurement) {
  element.position.forEach((point, index) => {
    const areas = isMeasurement ? this.measurementAreas : this.areas;
    const color = isMeasurement ? viewerColors.POINT_COLOR : viewerColors.SOLAR_POINT_COLOR;
    const isFirst = index === 0;
    if (isFirst) areas.push({ points: [], lines: [], element });

    const area = areas[areas.length - 1];
    const dot = this.createReactivePoint(point, color);

    this.scene.add(dot);
    if (!isFirst) {
      let firstPoint = area.points[area.points.length - 1];
      let secondPoint = dot;
      const lineGroup = this.createLineGroup(
        firstPoint,
        secondPoint,
        isMeasurement,
        true
      );
      area.lines.push(lineGroup);
    }

    if (index === element.position.length - 1) {
      let firstPoint = dot;
      let secondPoint = area.points[0];
      // this is for the corupt areas for beta, it should be removed after some time
      if (!secondPoint) {
        secondPoint = dot;
      }
      const lineGroup = this.createLineGroup(
        firstPoint,
        secondPoint,
        isMeasurement,
        true
      );
      area.lines.push(lineGroup);
    }
    area.points.push(dot);
  });
};

export const displayRestrictedAreaPoint = function (element, detectedArea) {
  element.forEach((point, index) => {
    const isFirst = index === 0;
    if (isFirst)
      detectedArea.restrictedAreas.push({ points: [], lines: [], element });

    const area =
      detectedArea.restrictedAreas[detectedArea.restrictedAreas.length - 1];
    const dot = this.createReactivePoint(point, viewerColors.RESTRICTED_AREA_COLOR);

    this.scene.add(dot);
    if (!isFirst) {
      let firstPoint = area.points[area.points.length - 1];
      let secondPoint = dot;

      const firstPointDynamic = new THREE.Vector3();
      const secondPointDynamic = new THREE.Vector3();
      firstPoint.getWorldPosition(firstPointDynamic);
      secondPoint.getWorldPosition(secondPointDynamic);
      let points = [firstPointDynamic, secondPointDynamic];

      const lineGroup = this.createReactiveThickLine(
        points,
        4.0,
        false,
        false,
        viewerColors.RESTRICTED_AREA_COLOR
      );
      this.scene.add(lineGroup);

      const tempLineGroupe = {
        line: lineGroup,
        firstPoint,
        secondPoint,
      };
      area.lines.push(tempLineGroupe);
    }
    if (index === element.length - 1) {
      let firstPoint = dot;
      let secondPoint = area.points[0];

      const firstPointDynamic = new THREE.Vector3();
      const secondPointDynamic = new THREE.Vector3();
      firstPoint.getWorldPosition(firstPointDynamic);
      secondPoint.getWorldPosition(secondPointDynamic);
      let points = [firstPointDynamic, secondPointDynamic];

      const lineGroup = this.createReactiveThickLine(
        points,
        4.0,
        false,
        false,
        viewerColors.RESTRICTED_AREA_COLOR
      );
      this.scene.add(lineGroup);
      const tempLineGroupe = {
        line: lineGroup,
        firstPoint,
        secondPoint,
      };
      area.lines.push(tempLineGroupe);
    }

    area.points.push(dot);
  });
};

export const clearMagnetEffect = function () {
  this.selectedPoint = null;
};

export const showSnapIcon = function () {
  const snapIcon = this.selectedPoint.children[0];
  snapIcon.element.style.visibility = "visible";
};

export const hideSnapIcon = function () {
  const snapIcon = this.selectedPoint?.children[0];
  if (snapIcon) snapIcon.element.style.visibility = "hidden";
};

export const removeSnapIcon = function (isMeasurement = false) {
  const firstPoint = isMeasurement
    ? this.measurementAreas[this.measurementAreas.length - 1].points[0]
    : this.areas[this.areas.length - 1].points[0];
  const snapIcon = firstPoint.children[1];
  if (snapIcon) {
    firstPoint.remove(snapIcon);
    snapIcon.element.remove();
  }
};

export const changeCursorToCrosshair = function () {
  this.renderer.domElement.style.cursor = "crosshair";
};

export const changeCursorToPointer = function () {
  this.renderer.domElement.style.cursor = "pointer";
};

export const changeCursorToGrab = function () {
  this.renderer.domElement.style.cursor = "grab";
};

export const changeCursorToBlocked = function () {
  this.renderer.domElement.style.cursor = "not-allowed";
};

export const changeCursorToMove = function () {
  this.renderer.domElement.style.cursor = "move";
};

export const changeCursorToLoading = function () {
  this.renderer.domElement.style.cursor = "wait";
};

export const getNeighboringPoints = function (pointIndex, area) {
  let neighborPoints = [];

  if (area.points.length < 4) return [];

  if (pointIndex === 0) {
    neighborPoints.push(area.points[area.points.length - 1]);
  } else {
    neighborPoints.push(area.points[pointIndex - 1]);
  }
  if (pointIndex === area.points.length - 1) {
    neighborPoints.push(area.points[0]);
  } else {
    neighborPoints.push(area.points[pointIndex + 1]);
  }
  return neighborPoints;
};

export const checkMergePoints = function (
  pointIndex,
  area,
  isMeasurement = true
) {
  const neighborPoints = this.getNeighboringPoints(pointIndex, area);
  const point = area.points[pointIndex];

  if (neighborPoints.length !== 2) return false;

  let mergedPoints = false;
  neighborPoints.forEach((neighborPoint) => {
    let distance;
    if (isMeasurement) {
      distance = point.position.distanceTo(neighborPoint.position);
    } else {
      const projectedPoint = new THREE.Vector3();
      const vectorPoint = new THREE.Vector3(
        point.position.x,
        point.position.y,
        point.position.z
      );
      area.trianglePlane.projectPoint(vectorPoint, projectedPoint);
      distance = projectedPoint.distanceTo(neighborPoint.position);
    }
    if (distance < 0.3) {
      neighborPoint.merge = true;

      this.mergePoints(pointIndex, neighborPoints, area, isMeasurement);
      mergedPoints = true;

      if (isMeasurement) {
        this.disableMeasurementPointDragMode();
        this.enableMeasurementPointDragMode();
      } else {
        this.disablePointDragMode();
        this.enablePointDragMode();
      }
    }
  });

  return mergedPoints;
};

export const mergePoints = function (
  pointToRemoveIndex,
  neighborPoints,
  area,
  isMeasurement
) {
  const oldPoints = [...area.points];
  this.updatePositionOnObject(
    oldPoints[pointToRemoveIndex],
    this.draggedAreaPoint.lastPosition
  );

  const newPoints = area.points.filter(
    (_, index) => index !== pointToRemoveIndex
  );
  area.points = newPoints;

  const instancedMesh = this.replacePointsWithInstancedMesh(
    newPoints,
    isMeasurement ? viewerColors.SELECTED_LINE_COLOR : viewerColors.SOLAR_POINT_COLOR,
    area.pointInstancedMesh
  );
  this.scene.add(instancedMesh);
  area.pointInstancedMesh = instancedMesh;

  for (let line of area.lines) {
    this.removeObjectFromScene(line.midPoint);
    if (isMeasurement) this.removeObjectFromScene(line.label);
  }

  this.reCreateLinesFromPoints(area, isMeasurement);

  const midpointInstancedMesh = this.replacePointsWithInstancedMesh(
    area.lines.map((line) => line.midPoint),
    isMeasurement ? viewerColors.SELECTED_LINE_COLOR : viewerColors.SOLAR_POINT_COLOR,
    area.midpointInstancedMesh,
    true
  );
  this.scene.add(midpointInstancedMesh);
  area.midpointInstancedMesh = midpointInstancedMesh;

  this.resetMergePoints(neighborPoints);

  this.undoStack.push({
    action: "MERGE_POINT",
    area,
    points: oldPoints,
    isMeasurement,
  });
  this.resetRedoStack();
};

export const reCreateLinesFromPoints = function (area, isMeasurement = true) {
  area.lines = [];
  for (let i = 0; i < area.points.length; i++) {
    let firstPoint = area.points[i];
    let secondPoint =
      i === area.points.length - 1 ? area.points[0] : area.points[i + 1];

    const lineGroup = this.createLineGroup(
      firstPoint,
      secondPoint,
      isMeasurement,
      isMeasurement,
      false,
      true
    );
    area.lines.push(lineGroup);
  }
};

export const resetMergePoints = function (array) {
  for (let item of array) {
    item.merge = false;
  }
};

export const pointBelongsToArea = function (point, area) {
  if (area.points.map((point) => point.uuid).includes(point.uuid)) return true;
  return false;
};

export const replacePointsWithInstancedMesh = function (
  points,
  color = viewerColors.POINT_COLOR,
  instancedMeshToReplace = null,
  isMidpoint = false
) {
  if (instancedMeshToReplace) {
    this.removeObjectFromScene(instancedMeshToReplace);
  }
  const geometry = new THREE.SphereGeometry(isMidpoint ? 0.075 : 0.1, 32, 32);

  let material;
  if (isMidpoint)
    material = this.createShaderMaterialForInstancedMesh(BLACK, color, 0.3);
  else material = this.createShaderMaterialForInstancedMesh(color, BLACK, 0.25);

  const instancedMesh = new InstancedMesh2(
    this.renderer,
    points.length,
    geometry,
    material
  );
  markAsAnnotation(instancedMesh, {
    renderOrder: RENDERING_ORDER.OUTER_POINT,
    depthBias: MARKER_DEPTH_BIAS_M,
  });
  instancedMesh.computeBVH();

  const tempPosition = new THREE.Vector3();
  const transformationMatrix = new THREE.Matrix4();

  for (let i = 0; i < points.length; i++) {
    instancedMesh.getMatrixAt(i, transformationMatrix);

    transformationMatrix.decompose(
      tempPosition,
      new THREE.Quaternion(),
      new THREE.Vector3(1, 1, 1)
    );

    tempPosition.set(
      points[i].position.x,
      points[i].position.y,
      points[i].position.z
    );

    transformationMatrix.compose(
      tempPosition,
      new THREE.Quaternion(),
      new THREE.Vector3(1, 1, 1)
    );
    instancedMesh.setMatrixAt(i, transformationMatrix);

    if (points[i].isObject3D) {
      this.removeObjectWithChildrenFromScene(points[i]);
    }
  }

  instancedMesh.instanceMatrix.needsUpdate = true;
  instancedMesh.computeBVH();

  return instancedMesh;
};

export const combineAreaLines = function (area, color = viewerColors.MIDPOINT_COLOR) {
  for (let line of area.lines) {
    this.removeObjectFromScene(line.line);
  }

  const combinedLine = this.createClosedThickLine({
    points: area.points,
    color,
  });
  this.scene.add(combinedLine);
  area.combinedLine = combinedLine;
};

export const updateAllReactivePoints = function () {
  const openAreas = this.areas.filter((area) => !area.closed);

  for (let area of openAreas) {
    for (let point of area.points) {
      if (point.visible) this.updateReactivePoint(point);
    }

    if (area.lines) {
      for (let line of area.lines) {
        if (line.midPoint && line.midPoint.visible)
          this.updateReactivePoint(line.midPoint, true);
      }
    }
  }

  const closedAreasWithRestrictedAreas = this.areas.filter(
    (area) => area.closed && area.restrictedAreas.length > 0
  );

  for (let area of closedAreasWithRestrictedAreas) {
    const openRestrictedAreas = area.restrictedAreas.filter((ra) => !ra.closed);

    for (let restrictedArea of openRestrictedAreas) {
      for (let point of restrictedArea.points) {
        if (point.visible) this.updateReactivePoint(point);
      }
    }
  }

  const openMeasurementAreas = this.measurementAreas.filter(
    (area) => !area.closed
  );
  for (let area of openMeasurementAreas) {
    for (let point of area.points) {
      if (point.visible) this.updateReactivePoint(point);
    }

    if (area.lines) {
      for (let line of area.lines) {
        if (line.midPoint && line.midPoint.visible)
          this.updateReactivePoint(line.midPoint, true);
      }
    }
  }

  for (let measurement of this.measurements) {
    if (measurement.firstPoint?.visible)
      this.updateReactivePoint(measurement.firstPoint);
    if (measurement.secondPoint?.visible)
      this.updateReactivePoint(measurement.secondPoint);
  }
};

export const updateReactivePoint = function (point, isMidPoint = false) {
  const zoomFactor = isMidPoint ? ZOOM_FACTOR : ZOOM_FACTOR + 10;
  let distance;

  if (this.isOrthographicCamera) {
    distance = (this.camera.top - this.camera.bottom) / this.camera.zoom;
  } else {
    distance = point.position.distanceTo(this.camera.position);
  }

  const pointScaleFactor = (1 / zoomFactor) * distance;
  point.scale.set(pointScaleFactor, pointScaleFactor, pointScaleFactor);
};

export const updateAllInstancedMeshPoints = function () {
  const closedAreas = this.areas.filter((area) => area.closed);

  for (let area of closedAreas) {
    if (area.pointInstancedMesh)
      this.updateInstancedMeshPoints(area.pointInstancedMesh);
    if (area.midpointInstancedMesh)
      this.updateInstancedMeshPoints(area.midpointInstancedMesh);

    const closedRestrictedAreas = area.restrictedAreas.filter(
      (ra) => ra.closed
    );
    for (let restrictedArea of closedRestrictedAreas) {
      if (restrictedArea.instancedMesh)
        this.updateInstancedMeshPoints(restrictedArea.instancedMesh);
    }
  }

  const closedMeasurementAreas = this.measurementAreas.filter(
    (area) => area.closed
  );

  for (let area of closedMeasurementAreas) {
    if (area.pointInstancedMesh)
      this.updateInstancedMeshPoints(area.pointInstancedMesh);
    if (area.midpointInstancedMesh)
      this.updateInstancedMeshPoints(area.midpointInstancedMesh);
  }
};

export const updateInstancedMeshPoints = function (instancedMesh) {
  const tempPosition = new THREE.Vector3();
  const transformationMatrix = new THREE.Matrix4();
  const isOrtho = this.isOrthographicCamera;
  const orthoDistance = isOrtho
    ? (this.camera.top - this.camera.bottom) / this.camera.zoom
    : 0;

  for (let i = 0; i < instancedMesh.instancesCount; i++) {
    instancedMesh.getMatrixAt(i, transformationMatrix);
    transformationMatrix.decompose(
      tempPosition,
      new THREE.Quaternion(),
      new THREE.Vector3(1, 1, 1)
    );
    const distance = isOrtho
      ? orthoDistance
      : tempPosition.distanceTo(this.camera.position);
    const scaleFactor = (1 / (ZOOM_FACTOR + 10)) * distance;
    transformationMatrix.compose(
      tempPosition,
      new THREE.Quaternion(),
      new THREE.Vector3(scaleFactor, scaleFactor, scaleFactor)
    );
    instancedMesh.setMatrixAt(i, transformationMatrix);
  }
  instancedMesh.instanceMatrix.needsUpdate = true;
  instancedMesh.computeBVH();
};

export const shouldThrottle = function () {
  const currentTime = performance.now();
  if (currentTime - this.lastUpdateTime < this.throttleInterval) return true;
  this.lastUpdateTime = currentTime;
  return false;
};

export const addPointToScene = function (position, color) {
  const SphereGeometry = new THREE.SphereGeometry(0.1, 32, 32);
  const material = new THREE.MeshBasicMaterial({ color });
  const point = new THREE.Mesh(SphereGeometry, material);
  point.position.copy(position);
  this.scene.add(point);
  markAsAnnotation(point, { renderOrder: 9999999 });

  return point;
};

export const setPointColor = function (point, color) {
  point.material.uniforms.innerColor.value.set(color);
  point.material.uniforms.innerColor.needsUpdate = true;
};


export const setLineColor = function (line, color) {
  line.material.color.setHex(color);
};

export const raycastModelPoint = function () {
  this.raycaster.setFromCamera(this.mouse, this.camera);
  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0],
    false
  );
  return intersects.length > 0 ? intersects[0].point : null;
};

export const setAreaAsClosed = function (
  firstPoint,
  area,
  isRestricted = false,
  color = viewerColors.MOUNTING_SURFACE_COLOR
) {
  this.selectedPoint = firstPoint;
  this.renderer.domElement.style.cursor = `none`;
  area.closeArea = true;
  if (!isRestricted) this.dashedMeasurementLine.material.color.setHex(color);
  this.showSnapIcon();

  if (this.areaEndingLine) this.areaEndingLine.visible = false;
};

export const setAreaAsOpen = function (area, color = viewerColors.SOLAR_POINT_COLOR) {
  area.closeArea = false;
  if (this.selectedPoint) {
    this.dashedMeasurementLine.material.color.setHex(color);
    if (this.areaEndingLine) this.areaEndingLine.visible = true;
    this.hideSnapIcon();
    this.selectedPoint = null;
    this.changeCursorToCrosshair();
  }
};

